import {
  getBalltrackerByMachineUuidUnifiedOptions,
  getMachinesSearchOptions,
  getMachinesSearchTaggingOptions,
  MachinePublicHydrated,
  OrgRoles,
  Roles,
  UnifiedConfig,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useAppContext } from "@/hooks/use-app-context";
import { isMagnusAdminUser } from "@/lib/permissions";
import { useComplexesScheduledEventsCount } from "@/pages/Complexes/SingleField/FieldSchedule/FieldSchedule.queries";
import { Complex } from "@/types/game";

// Machines without an assigned `complex` are grouped by their `region` so they
// still appear under a recognizable parent on the complexes screens and so
// routes built from this name resolve back to a real bucket.
export const getComplexName = (
  machine: Pick<MachinePublicHydrated, "complex" | "region">,
): string => machine.complex ?? machine.region;

// Polled so the complex/field screens reflect live state (active events, ping
// freshness, LED windows) without requiring tab focus. React-query gates
// `refetchInterval` to visible tabs by default, so background tabs don't poll.
const MACHINES_REFETCH_INTERVAL_MS = 30_000;

// Determine if user should use tagging endpoint (false for magnus admins, true otherwise)
const useIsTaggingMachines = (): boolean => {
  const { user, currentOrganization } = useAppContext();

  return useMemo(() => {
    const userRoles = [
      ...(user?.roles ?? []),
      ...(currentOrganization?.orgRoles ?? []),
    ] as (Roles | OrgRoles)[];
    return !isMagnusAdminUser(userRoles);
  }, [user?.roles, currentOrganization?.orgRoles]);
};

// Snapshot time of the shared machines query, in epoch ms. Components that
// derive health from a `MachinePublicHydrated` should compare ping freshness
// against this, not `Date.now()` — otherwise a tab whose data hasn't refetched
// drifts into a false "Offline" once local time outruns the cached snapshot.
// Returns `Date.now()` until the first successful fetch lands.
export const useMachinesAsOf = (): number => {
  const isTaggingMachines = useIsTaggingMachines();

  const queryOptions = isTaggingMachines
    ? getMachinesSearchTaggingOptions({})
    : getMachinesSearchOptions({});

  const { dataUpdatedAt } = useQuery({
    ...queryOptions,
    refetchInterval: MACHINES_REFETCH_INTERVAL_MS,
  });
  return dataUpdatedAt || Date.now();
};

export const useComplexes = (
  options: { includeScheduledCounts?: boolean } = {},
) => {
  const { includeScheduledCounts = false } = options;
  const isTaggingMachines = useIsTaggingMachines();

  const queryOptions = isTaggingMachines
    ? getMachinesSearchTaggingOptions({})
    : getMachinesSearchOptions({});

  const { data, isLoading, dataUpdatedAt } = useQuery({
    ...queryOptions,
    select: (result) => result?.machines,
    refetchInterval: MACHINES_REFETCH_INTERVAL_MS,
  });

  const baseComplexes: Complex[] | undefined = useMemo(() => {
    if (!data) return undefined;

    const uniqueComplexesNames = Array.from(
      new Set(data.map((i) => getComplexName(i))),
    );

    const complexesWithFields: Complex[] = uniqueComplexesNames.map(
      (complexName) => {
        const fields = data.filter(
          (machine) => getComplexName(machine) === complexName,
        );
        return {
          name: complexName,
          fields,
          fieldCount: fields.length,
          activeEventsCount: fields.filter((machine) => machine.currentGame)
            .length,
          scheduledEventsCount: undefined,
          location: fields[0]?.region ?? "n/a",
        };
      },
    );

    return complexesWithFields;
  }, [data]);

  const scheduledCounts = useComplexesScheduledEventsCount(
    includeScheduledCounts ? baseComplexes : undefined,
  );

  const complexes = useMemo(() => {
    if (!baseComplexes) return undefined;
    return baseComplexes.map((complex) => ({
      ...complex,
      scheduledEventsCount: includeScheduledCounts
        ? scheduledCounts.get(complex.name)
        : undefined,
    }));
  }, [baseComplexes, scheduledCounts, includeScheduledCounts]);

  return { complexes, isLoading, dataUpdatedAt };
};

export const useSingleComplex = (complexName?: string) => {
  const { complexes, isLoading, dataUpdatedAt } = useComplexes();

  const complex = useMemo(() => {
    return complexes?.find((c) => c.name === complexName) ?? null;
  }, [complexName, complexes]);

  return { complex, isLoading, dataUpdatedAt };
};

export const useField = (fieldId: string) => {
  const { data: unifiedConfig, isLoading: isLoadingUnifiedConfig } = useQuery({
    ...getBalltrackerByMachineUuidUnifiedOptions({
      path: { machine_uuid: fieldId ?? "" },
    }),
    enabled: !!fieldId,
    retry: true,
    select: (data) =>
      "body" in data ? (data.body as UnifiedConfig) : undefined,
  });

  const field = unifiedConfig?.machine ?? undefined;
  const strikeZoneConfig = unifiedConfig?.strikeZone ?? undefined;

  return {
    field,
    unifiedConfig,
    strikeZoneConfig,
    isLoadingField: isLoadingUnifiedConfig,
    isLoadingUnifiedConfig,
    isLoadingStrikeZoneConfig: isLoadingUnifiedConfig,
  };
};
